import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil, filter } from 'rxjs/operators';

import { MatSidenavModule, MatSidenav, MatDrawerContainer } from '@angular/material/sidenav';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { ThemeService, FeatureFlagService } from '../../core';
import { HeaderComponent } from '../header/header.component';
import { NavigationComponent } from '../navigation/navigation.component';
import { IhTabbarComponent } from '../ih-tabbar/ih-tabbar.component';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    MatSidenavModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    HeaderComponent,
    NavigationComponent,
    IhTabbarComponent
  ],
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.scss']
})
export class AppShellComponent implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer!: MatSidenav;
  
  private destroy$ = new Subject<void>();
  
  // Responsive breakpoints - initialized in ngOnInit
  isHandset$!: Observable<boolean>;
  isTablet$!: Observable<boolean>;
  isDesktop$!: Observable<boolean>;

  // Theme and feature flags - initialized in ngOnInit
  currentTheme$!: Observable<any>;
  enableMobileNavigation$!: Observable<boolean>;

  // Navigation state
  sidenavMode: 'over' | 'push' | 'side' = 'side';
  sidenavOpened = true;
  showBackdrop = false;

  // IH* Mobile Pages Detection - EmptyLayout equivalent
  isIHMobilePage = false;

  // IHUB redesign bottom tab bar — shown only on the 5 main tab roots (so it
  // auto-hides on sub-pages: detail / forms / success), and only when the
  // `enable_new_ui` flag is on.
  showTabBar = false;
  private readonly mainTabRoots = ['/ih-home', '/ih-policies', '/ih-pay', '/ih-bonus-history', '/ih-profile'];

  get useNewUi(): boolean {
    return this.featureFlagService.isEnabled('enable_new_ui');
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    private themeService: ThemeService,
    private featureFlagService: FeatureFlagService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initializeObservables();
    this.setupResponsiveNavigation();
    this.detectIHMobilePages();
  }

  private initializeObservables(): void {
    // Initialize responsive breakpoints
    this.isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset)
      .pipe(
        map(result => result.matches),
        shareReplay()
      );

    this.isTablet$ = this.breakpointObserver.observe([Breakpoints.Tablet])
      .pipe(
        map(result => result.matches),
        shareReplay()
      );

    this.isDesktop$ = this.breakpointObserver.observe([
      Breakpoints.Large,
      Breakpoints.XLarge
    ]).pipe(
      map(result => result.matches),
      shareReplay()
    );

    // Initialize theme and feature flags
    this.currentTheme$ = this.themeService.currentTheme$;
    this.enableMobileNavigation$ = this.featureFlagService.config$.pipe(
      map(config => config['enable_mobile_navigation'] || false)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Toggle the navigation drawer
   */
  toggleNavigation(): void {
    if (this.drawer) {
      this.drawer.toggle();
    }
  }

  /**
   * Close the navigation drawer
   */
  closeNavigation(): void {
    if (this.drawer && this.drawer.opened) {
      this.drawer.close();
    }
  }

  /**
   * Handle navigation link clicks
   */
  onNavigationClick(): void {
    // Close drawer on mobile after navigation
    if (this.sidenavMode === 'over') {
      this.closeNavigation();
    }
  }

  /**
   * Toggle theme between light and dark
   */
  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  private setupResponsiveNavigation(): void {
    // Handle handset (mobile) breakpoint
    this.isHandset$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isHandset => {
      if (isHandset) {
        this.sidenavMode = 'over';
        this.sidenavOpened = false;
        this.showBackdrop = true;
      } else {
        this.sidenavMode = 'side';
        this.sidenavOpened = true;
        this.showBackdrop = false;
      }
      this.cdr.detectChanges();
    });

    // Handle tablet breakpoint
    this.isTablet$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isTablet => {
      if (isTablet && !this.breakpointObserver.isMatched(Breakpoints.Handset)) {
        this.sidenavMode = 'push';
        this.sidenavOpened = true;
        this.showBackdrop = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Detect IH* mobile pages (EmptyLayout equivalent)
   * IH* pages should not show header/navigation like Blazor @layout EmptyLayout
   */
  private detectIHMobilePages(): void {
    // Check initial route
    this.isIHMobilePage = this.checkIfIHMobilePage(this.router.url);
    this.showTabBar = this.checkShowTabBar(this.router.url);

    // Listen to navigation changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: any) => {
      const url = event.urlAfterRedirects || event.url;
      this.isIHMobilePage = this.checkIfIHMobilePage(url);
      this.showTabBar = this.checkShowTabBar(url);
      this.cdr.detectChanges();
    });
  }

  /**
   * Check if current route is an IH* mobile page
   */
  private checkIfIHMobilePage(url: string): boolean {
    return url.startsWith('/ih-');
  }

  /**
   * Tab bar visibility: only on the main tab roots, only when the new UI is on.
   * Strips query/fragment so e.g. /ih-pay?x=1 still matches but /ih-pay-premium
   * (a sub-page) does not.
   */
  private checkShowTabBar(url: string): boolean {
    if (!this.useNewUi) { return false; }
    const path = url.split('?')[0].split('#')[0];
    return this.mainTabRoots.includes(path);
  }
}